<script>
import PageNavigation from '@components/admin/page-navigation'
import date from 'locutus/php/datetime/date'
import strtotime from 'locutus/php/datetime/strtotime'
import long2ip from 'locutus/php/network/long2ip'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'
import { fetchAccountTransferList } from '@apis/admin/account'
import { mapState } from 'vuex'

export default {
  components: { PageNavigation },
  data() {
    return {
      loading: false,
      list: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      excel_headers: [
        ['seq', this.$t('account_transfer_list.seq')],
        ['asset_seq', this.$t('account_transfer_list.asset_seq')],
        ['from_seq', this.$t('account_transfer_list.from_seq')],
        ['to_seq', this.$t('account_transfer_list.to_seq')],
        ['amount', this.$t('account_transfer_list.amount')],
        ['from_member_seq', this.$t('account_transfer_list.from_member_seq')],
        ['to_member_seq', this.$t('account_transfer_list.to_member_seq')],
        [
          'from_account_number',
          this.$t('account_transfer_list.from_account_number'),
        ],
        [
          'to_account_number',
          this.$t('account_transfer_list.to_account_number'),
        ],
        ['from_user_name', this.$t('account_transfer_list.from_user_name')],
        ['to_user_name', this.$t('account_transfer_list.to_user_name')],
        ['asset_title', this.$t('account_transfer_list.asset_title')],
        ['asset_code', this.$t('account_transfer_list.asset_code')],
        ['regdate', this.$t('account_transfer_list.regdate')],
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('admin.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchAccountTransferList({
        ...this.$route.params,
        ...this.$route.query,
      })
      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.msg_excel_warning'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchAccountTransferList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('account_transfer_list.transfer_request_list', [
            date('YmdHis'),
          ]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    date,
    strtotime,
    long2ip,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">
      {{ $t('admin.search') }}
    </h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['from_user_id', $t('admin.from_user_id')],
                      ['from_email_address', $t('admin.from_email_address')],
                      ['from_user_name', $t('admin.from_user_name')],
                      ['to_user_id', $t('admin.from_user_id')],
                      ['to_email_address', $t('admin.from_email_address')],
                      ['to_user_name', $t('admin.to_user_name')],
                      ['from_account_number', $t('admin.from_account_number')],
                      ['to_account_number', $t('admin.to_account_number')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.from_use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('admin.from_use_statistics_exclude') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.to_use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('admin.to_use_statistics_exclude') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.from_member_is_alive"
                :false-label="undefined"
                true-label="Y"
              >
                {{ $t('admin.from_member_is_alive') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.to_member_is_alive"
                :false-label="undefined"
                true-label="Y"
              >
                {{ $t('admin.to_member_is_alive') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['last_login', $t('admin.last_login')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="
                  search_params = {}
                  search
                "
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin.transfer_list') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <ul v-if="0" :class="$style.localNavigation">
      <router-link
        v-for="{ title, seq } in [
          { title: $t('admin.all_show'), seq: undefined },
          ...assets,
        ]"
        :key="seq"
        :class="{ [$style.on]: seq == search_params.asset_seq }"
        :to="{ query: { ...$route.query, asset_seq: seq } }"
        tag="li"
      >
        <a href="#" @click.prevent>{{ title }}</a>
      </router-link>
    </ul>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('account_transfer_list.from_user_name') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{
                $t('account_transfer_list.from_account_number')
              }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('account_transfer_list.to_user_name') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('account_transfer_list.to_account_number') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.asset_title') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.modal_asset_code') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.request_amount') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.regdate') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(data, idx) in list"
          :key="data.seq"
          :class="$style[`bg${idx % 2}`]"
        >
          <td :class="[$style.tCenter, $style.nowrap]">{{ data._no }}</td>
          <template v-if="!$route.params.member_seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.from_user_name
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.from_account_number
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.to_user_name
            }}</td>
          </template>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.to_account_number
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.asset_title
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.asset_code
          }}</td>
          <td :class="[$style.tRight, $style.nowrap]">{{ data.amount }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            date('Y-m-d H:i:s', new Date(data.regdate))
          }}</td>
        </tr>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 8 : 11"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 8 : 11"
            :class="$style.tCenter"
            >{{ $t('admin.msg_search_transfer_list') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
